import { APP_WS_ENDPOINT } from "@API/index";
import { getAuthToken } from "@Security/stateHelpers";
import { downloadFileByPath } from "@Dao/api";

/**
 * Типы ответов от WebSocket
 */
const RESPONSE_TYPES = {
  FILE: "file",
  JSON: "json",
};

/**
 * Опции для WebSocket подключения
 * @typedef {Object} WebSocketOptions
 * @property {string} endpoint - Путь к WebSocket эндпоинту (без базового URL)
 * @property {Object} [params] - Параметры для добавления в URL
 * @property {Function} [onProgress] - Callback для отслеживания прогресса (получает текст статуса)
 * @property {Function} [onError] - Callback для обработки ошибок
 * @property {Function} [onSuccess] - Callback для успешного получения данных
 * @property {number} [timeout] - Таймаут подключения в мс (по умолчанию 30000)
 */

/**
 * Основная функция для подключения к WebSocket и получения данных
 * @param {WebSocketOptions} options - Опции подключения
 * @returns {Promise<{type: string, data: any, fileName?: string}>} - Результат операции
 */
export const connectWebSocket = (options) => {
  return new Promise((resolve, reject) => {
    const {
      endpoint,
      params = {},
      onProgress,
      onError,
      onSuccess,
      timeout = 600000,
    } = options;

    // Формируем URL с параметрами
    let socketURL = `${APP_WS_ENDPOINT()}${endpoint}`;
    const urlParams = new URLSearchParams(params);
    if (urlParams.toString()) {
      socketURL += `?${urlParams.toString()}`;
    }

    let timeoutId;
    let ws;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };

    try {
      ws = new WebSocket(socketURL);

      // Устанавливаем таймаут
      timeoutId = setTimeout(() => {
        cleanup();
        const error = new Error("WebSocket connection timeout");
        onError?.(error);
        reject(error);
      }, timeout);

      const accessToken = getAuthToken();

      ws.onopen = () => {
        // Отправляем токен авторизации
        const authMessage = JSON.stringify({
          accessToken: accessToken,
        });
        ws.send(authMessage);
        onProgress?.("Подключение установлено, авторизация...");
      };

      ws.onmessage = (event) => {
        try {
          // Проверяем, является ли сообщение ping или другим служебным сообщением
          if (event.data === "ping" || event.data === "pong") {
            // Игнорируем ping/pong сообщения
            return;
          }

          // Проверяем, является ли сообщение JSON
          let response;
          try {
            response = JSON.parse(event.data);
          } catch (parseError) {
            // Если это не JSON, проверяем, не является ли это служебным сообщением
            if (
              typeof event.data === "string" &&
              (event.data.includes("ping") ||
                event.data.includes("pong") ||
                event.data.trim() === "")
            ) {
              // Игнорируем служебные сообщения
              return;
            }
            // Если это что-то другое, выбрасываем ошибку
            throw new Error(`Получено неожиданное сообщение: ${event.data}`);
          }

          // Проверяем на ошибки
          if (!response.success) {
            const errorMessage =
              response.errors && response.errors.length > 0
                ? response.errors.join(", ")
                : response.message || "Произошла ошибка при получении данных";

            const error = new Error(errorMessage);
            onError?.(error);
            cleanup();
            reject(error);
            return;
          }

          // Обрабатываем статус прогресса
          if (response.data === "Processing started") {
            onProgress?.("Обработка начата...");
            return;
          }

          // Проверяем, это файл или JSON данные
          if (
            response.data?.content &&
            response.data?.fileName &&
            response.data?.contentType
          ) {
            // Это файл - обрабатываем base64 контент
            const content = response.data.content
              .replace(/\\u002B/g, "+")
              .replace(/\\u002F/g, "/")
              .replace(/\\u003D/g, "=");

            const byteCharacters = atob(content);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
              type: response.data.contentType,
            });

            const url = URL.createObjectURL(blob);

            const result = {
              type: RESPONSE_TYPES.FILE,
              data: blob,
              fileName: response.data.fileName,
              contentType: response.data.contentType,
              url: url,
            };

            onSuccess?.(result);
            cleanup();
            resolve(result);
          } else {
            const result = {
              type: RESPONSE_TYPES.JSON,
              data: response.data,
            };

            onSuccess?.(result);
            cleanup();
            resolve(result);
          }
        } catch (parseError) {
          const error = new Error(
            `Ошибка парсинга ответа: ${parseError.message}`
          );
          onError?.(error);
          cleanup();
          reject(error);
        }
      };

      ws.onerror = (error) => {
        const wsError = new Error("Ошибка WebSocket соединения");
        onError?.(wsError);
        cleanup();
        reject(wsError);
      };

      ws.onclose = (event) => {
        if (!event.wasClean) {
          const error = new Error(
            `WebSocket соединение закрыто с ошибкой: код ${event.code}`
          );
          onError?.(error);
          cleanup();
          reject(error);
        }
      };
    } catch (error) {
      const initError = new Error(
        `Ошибка инициализации WebSocket: ${error.message}`
      );
      onError?.(initError);
      cleanup();
      reject(initError);
    }
  });
};

/**
 * Упрощенная функция для скачивания файла через WebSocket
 * @param {string} endpoint - Путь к WebSocket эндпоинту
 * @param {Object} params - Параметры для URL
 * @param {string} [fileName] - Имя файла для сохранения (если не указано, будет взято из ответа)
 * @returns {Promise<void>}
 */
export const downloadFileViaWebSocket = async (
  endpoint,
  params = {},
  fileName = null
) => {
  try {
    const result = await connectWebSocket({
      endpoint,
      params,
      onProgress: (status) => console.log("Download progress:", status),
    });

    if (result.type === RESPONSE_TYPES.FILE) {
      // Декодируем unicode символы в имени файла
      let downloadFileName = fileName || result.fileName;
      if (downloadFileName) {
        // Декодируем unicode escape последовательности
        downloadFileName = downloadFileName.replace(
          /\\u[\dA-F]{4}/gi,
          (match) => {
            return String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16));
          }
        );
      }

      downloadFileByPath(result.url, downloadFileName);
      // Освобождаем память
      setTimeout(() => URL.revokeObjectURL(result.url), 1000);
    } else {
      throw new Error("Ожидался файл, но получены JSON данные");
    }
  } catch (error) {
    console.error("Ошибка скачивания файла:", error);
    throw error;
  }
};

/**
 * Упрощенная функция для получения JSON данных через WebSocket
 * @param {string} endpoint - Путь к WebSocket эндпоинту
 * @param {Object} params - Параметры для URL
 * @returns {Promise<any>} - JSON данные
 */
export const getJsonViaWebSocket = async (endpoint, params = {}) => {
  try {
    const result = await connectWebSocket({
      endpoint,
      params,
      onProgress: (status) => console.log("Data loading progress:", status),
    });

    if (result.type === RESPONSE_TYPES.JSON) {
      return result.data;
    } else {
      throw new Error("Ожидались JSON данные, но получен файл");
    }
  } catch (error) {
    console.error("Ошибка получения JSON данных:", error);
    throw error;
  }
};

export { RESPONSE_TYPES };
